/**
 * Champion Application Review Interface
 * Phase 7C: Admin panel for reviewing and scoring Champion applications
 * Phase 7D: Integrated with email templates and sending service
 *
 * Features:
 * - Stats dashboard (Total, Pending, Scored, Accepted, Avg Score)
 * - Search & filters (by name, email, organizations; filter by status)
 * - Applications list with score badges
 * - Scoring rubric interface (6 sliders, 0-100 points)
 * - Application detail view (full responses from all 8 steps)
 * - Review notes textarea
 * - Decision buttons with email preview (Accept/Waitlist/Reject)
 * - Email preview modal before sending
 */

import { useState, useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import {
  Search,
  Filter,
  Award,
  Users,
  Clock,
  CheckCircle2,
  TrendingUp,
  Mail,
  Eye,
  Send,
  X,
  AlertTriangle,
} from 'lucide-react';

// Local type definition (TODO: move to @lake-charles/types when module is created)
interface ChampionApplicationSubmission {
  applicationId?: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  yearsInLakeCharles?: string;
  organizations?: string[];
  leadershipRoles?: string;
  volunteerWork?: string;
  neighborsReachable?: string;
  socialMediaFollowing?: string;
  communityConnections?: string;
  feedbackStrategy?: string;
  feedbackFrequency?: string;
  platformDescription?: string;
  hoursPerMonth?: string;
  availability?: string;
  governanceInterest?: string;
  governanceRoles?: string;
  reference1Name?: string;
  reference1Contact?: string;
  reference2Name?: string;
  reference2Contact?: string;
  status?: 'pending' | 'accepted' | 'waitlisted' | 'rejected';
  submittedAt?: string;
}

// TODO: Email services not yet implemented in Lake Charles backend
// Uncomment when emailService and emailTemplates are available
// import {
//   sendAcceptanceEmail,
//   sendWaitlistEmail,
//   sendRejectionEmail,
//   sendOnboardingEmail,
//   previewEmailTemplate
// } from '@lake-charles/services/emailService';
// import {
//   generateAcceptanceEmail,
//   generateWaitlistEmail,
//   generateRejectionEmail,
//   generateOnboardingEmail,
//   generateTemporaryPassword
// } from '@lake-charles/services/emailTemplates';

// Stub functions for email services (not implemented yet)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateAcceptanceEmail = (_app: ChampionApplicationSubmission, _score: number) => ({
  htmlBody: '<p>Email service not implemented</p>',
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateWaitlistEmail = (_app: ChampionApplicationSubmission, _score: number) => ({
  htmlBody: '<p>Email service not implemented</p>',
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateRejectionEmail = (_app: ChampionApplicationSubmission, _score: number) => ({
  htmlBody: '<p>Email service not implemented</p>',
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateOnboardingEmail = (_app: ChampionApplicationSubmission, _credentials: unknown) => ({
  htmlBody: '<p>Email service not implemented</p>',
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateTemporaryPassword = (_name: string) => 'TEMP_PASSWORD_123';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sendAcceptanceEmail = async (_app: ChampionApplicationSubmission, _score: number) => ({
  success: true,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sendWaitlistEmail = async (_app: ChampionApplicationSubmission, _score: number) => ({
  success: true,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sendRejectionEmail = async (_app: ChampionApplicationSubmission, _score: number) => ({
  success: true,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sendOnboardingEmail = async (_app: ChampionApplicationSubmission) => ({ success: true });

interface ApplicationScore {
  applicationId: string;
  communityInvolvement: number; // 0-30 points
  reachPotential: number; // 0-20 points
  feedbackPlan: number; // 0-20 points
  communicationSkills: number; // 0-15 points
  timeCommitment: number; // 0-10 points
  longTermInterest: number; // 0-5 points
  totalScore: number; // Sum of all (0-100)
  reviewerNotes: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export function ChampionApplicationReview() {
  const [applications, setApplications] = useState<ChampionApplicationSubmission[]>(() => {
    const stored = localStorage.getItem('champion_applications');
    return stored ? JSON.parse(stored) : [];
  });

  const [scores, setScores] = useState<Record<string, ApplicationScore>>(() => {
    const stored = localStorage.getItem('champion_scores');
    return stored ? JSON.parse(stored) : {};
  });

  const [selectedApplication, setSelectedApplication] =
    useState<ChampionApplicationSubmission | null>(null);
  const [currentScore, setCurrentScore] = useState<ApplicationScore>({
    applicationId: '',
    communityInvolvement: 0,
    reachPotential: 0,
    feedbackPlan: 0,
    communicationSkills: 0,
    timeCommitment: 0,
    longTermInterest: 0,
    totalScore: 0,
    reviewerNotes: '',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewType, setEmailPreviewType] = useState<
    'acceptance' | 'waitlist' | 'rejection' | 'onboarding'
  >('acceptance');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter((a) => !scores[a.applicationId!]).length;
    const scored = applications.filter((a) => scores[a.applicationId!]).length;
    const accepted = applications.filter((a) => a.status === 'accepted').length;
    const scoredApplications = applications.filter((a) => scores[a.applicationId!]);
    const avgScore =
      scoredApplications.length > 0
        ? scoredApplications.reduce(
            (sum, a) => sum + (scores[a.applicationId!]?.totalScore || 0),
            0,
          ) / scoredApplications.length
        : 0;

    return { total, pending, scored, accepted, avgScore: avgScore.toFixed(1) };
  }, [applications, scores]);

  // Filter and search applications
  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'pending' && scores[app.applicationId!]) return false;
        if (statusFilter === 'scored' && !scores[app.applicationId!]) return false;
        if (statusFilter !== 'pending' && statusFilter !== 'scored' && app.status !== statusFilter)
          return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          app.name.toLowerCase().includes(query) ||
          app.email.toLowerCase().includes(query) ||
          app.organizations?.some((org) => org.toLowerCase().includes(query))
        );
      }

      return true;
    });
  }, [applications, searchQuery, statusFilter, scores]);

  // Load application and its score
  const handleSelectApplication = (app: ChampionApplicationSubmission) => {
    setSelectedApplication(app);
    const existingScore = scores[app.applicationId!];
    if (existingScore) {
      setCurrentScore(existingScore);
    } else {
      setCurrentScore({
        applicationId: app.applicationId!,
        communityInvolvement: 0,
        reachPotential: 0,
        feedbackPlan: 0,
        communicationSkills: 0,
        timeCommitment: 0,
        longTermInterest: 0,
        totalScore: 0,
        reviewerNotes: '',
      });
    }
  };

  // Update score slider
  const handleScoreChange = (field: keyof ApplicationScore, value: number) => {
    const updatedScore = { ...currentScore, [field]: value };
    // Recalculate total
    updatedScore.totalScore =
      updatedScore.communityInvolvement +
      updatedScore.reachPotential +
      updatedScore.feedbackPlan +
      updatedScore.communicationSkills +
      updatedScore.timeCommitment +
      updatedScore.longTermInterest;
    setCurrentScore(updatedScore);
  };

  // Save score to localStorage
  const handleSaveScore = () => {
    if (!selectedApplication) return;

    const updatedScores = {
      ...scores,
      [selectedApplication.applicationId!]: {
        ...currentScore,
        reviewedAt: new Date().toISOString(),
        reviewedBy: 'Admin', // TODO: Get from auth context
      },
    };

    setScores(updatedScores);
    localStorage.setItem('champion_scores', JSON.stringify(updatedScores));
    alert('Score saved successfully!');
  };

  // Preview email before sending
  const handlePreviewEmail = (type: 'acceptance' | 'waitlist' | 'rejection' | 'onboarding') => {
    if (!selectedApplication) return;

    const score = currentScore.totalScore;
    let template;

    switch (type) {
      case 'acceptance':
        template = generateAcceptanceEmail(selectedApplication, score);
        break;
      case 'waitlist':
        template = generateWaitlistEmail(selectedApplication, score);
        break;
      case 'rejection':
        template = generateRejectionEmail(selectedApplication, score);
        break;
      case 'onboarding':
        const credentials = {
          email: selectedApplication.email,
          temporaryPassword: generateTemporaryPassword(selectedApplication.name),
          dashboardUrl: 'http://localhost:5175/champions/dashboard',
          resetPasswordUrl: 'http://localhost:5175/champions/reset-password',
        };
        template = generateOnboardingEmail(selectedApplication, credentials);
        break;
    }

    setEmailPreviewType(type);
    setEmailPreviewHtml(template.htmlBody);
    setShowEmailPreview(true);
  };

  // Send email and update status
  const handleSendEmailAndUpdateStatus = async (
    type: 'acceptance' | 'waitlist' | 'rejection',
    newStatus: 'accepted' | 'waitlisted' | 'rejected',
  ) => {
    if (!selectedApplication) return;

    // Validate score exists
    if (!scores[selectedApplication.applicationId!]) {
      alert('Please score this application before making a decision.');
      return;
    }

    setSendingEmail(true);
    const score = currentScore.totalScore;

    try {
      let result;
      switch (type) {
        case 'acceptance':
          result = await sendAcceptanceEmail(selectedApplication, score);
          break;
        case 'waitlist':
          result = await sendWaitlistEmail(selectedApplication, score);
          break;
        case 'rejection':
          result = await sendRejectionEmail(selectedApplication, score);
          break;
      }

      if (result.success) {
        // Update application status
        const updatedApplications = applications.map((app) =>
          app.applicationId === selectedApplication.applicationId
            ? { ...app, status: newStatus }
            : app,
        );
        setApplications(updatedApplications);
        localStorage.setItem('champion_applications', JSON.stringify(updatedApplications));

        // If accepted, send onboarding email too
        if (newStatus === 'accepted') {
          await sendOnboardingEmail(selectedApplication);
        }

        alert(`${type.charAt(0).toUpperCase() + type.slice(1)} email sent successfully!`);
        setShowEmailPreview(false);
      } else {
        alert(`Failed to send email: ${result.error}`);
      }
    } catch (error: any) {
      alert(`Error sending email: ${error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  // Get score badge color
  const getScoreBadgeColor = (score: number) => {
    if (score >= 85) return 'bg-green-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 55) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Champion Application Review</h1>
        <p className="text-gray-600 mt-1">
          Review, score, and make decisions on Champion applications
        </p>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
            </div>
            <Clock className="h-8 w-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Scored</p>
              <p className="text-2xl font-bold text-blue-600">{stats.scored}</p>
            </div>
            <Award className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Score</p>
              <p className="text-2xl font-bold text-purple-600">{stats.avgScore}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg p-4 shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Review</option>
              <option value="scored">Scored</option>
              <option value="accepted">Accepted</option>
              <option value="waitlisted">Waitlisted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content: Applications List + Detail View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Applications List (Left Panel) */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">
              Applications ({filteredApplications.length})
            </h2>
          </div>
          <div className="overflow-y-auto max-h-[calc(100vh-400px)]">
            {filteredApplications.map((app) => {
              const appScore = scores[app.applicationId!];
              const isSelected = selectedApplication?.applicationId === app.applicationId;

              return (
                <div
                  key={app.applicationId}
                  onClick={() => handleSelectApplication(app)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{app.name}</h3>
                      <p className="text-sm text-gray-600">{app.email}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted {new Date(app.submittedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {appScore && (
                      <div
                        className={`px-2 py-1 rounded text-white text-sm font-medium ${getScoreBadgeColor(appScore.totalScore)}`}
                      >
                        {appScore.totalScore}
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    {app.status === 'accepted' && (
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        Accepted
                      </span>
                    )}
                    {app.status === 'waitlisted' && (
                      <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                        Waitlisted
                      </span>
                    )}
                    {app.status === 'rejected' && (
                      <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                        Rejected
                      </span>
                    )}
                    {!appScore && (
                      <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                        Pending Review
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail View (Right Panel) */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          {selectedApplication ? (
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedApplication.name}</h2>
                  <p className="text-gray-600">{selectedApplication.email}</p>
                  <p className="text-sm text-gray-500">
                    Applied {new Date(selectedApplication.submittedAt).toLocaleDateString()}
                  </p>
                </div>
                {scores[selectedApplication.applicationId!] && (
                  <div
                    className={`px-4 py-2 rounded-lg text-white font-bold text-xl ${getScoreBadgeColor(currentScore.totalScore)}`}
                  >
                    {currentScore.totalScore}/100
                  </div>
                )}
              </div>

              {/* Scoring Interface */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Scoring Rubric</h3>

                {/* Community Involvement: 0-30 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Community Involvement
                    </label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.communityInvolvement}/30
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    value={currentScore.communityInvolvement}
                    onChange={(e) =>
                      handleScoreChange('communityInvolvement', Number(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Reach Potential: 0-20 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Reach Potential</label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.reachPotential}/20
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={currentScore.reachPotential}
                    onChange={(e) => handleScoreChange('reachPotential', Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Feedback Plan: 0-20 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Feedback Plan</label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.feedbackPlan}/20
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={currentScore.feedbackPlan}
                    onChange={(e) => handleScoreChange('feedbackPlan', Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Communication Skills: 0-15 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Communication Skills
                    </label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.communicationSkills}/15
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="15"
                    value={currentScore.communicationSkills}
                    onChange={(e) =>
                      handleScoreChange('communicationSkills', Number(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Time Commitment: 0-10 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Time Commitment</label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.timeCommitment}/10
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={currentScore.timeCommitment}
                    onChange={(e) => handleScoreChange('timeCommitment', Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Long-term Interest: 0-5 points */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Long-term Interest</label>
                    <span className="text-sm font-semibold text-gray-900">
                      {currentScore.longTermInterest}/5
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    value={currentScore.longTermInterest}
                    onChange={(e) => handleScoreChange('longTermInterest', Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                {/* Total Score Display */}
                <div className="pt-4 border-t border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Score</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {currentScore.totalScore}/100
                    </span>
                  </div>
                </div>
              </div>

              {/* Review Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Notes</label>
                <textarea
                  value={currentScore.reviewerNotes}
                  onChange={(e) =>
                    setCurrentScore({ ...currentScore, reviewerNotes: e.target.value })
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add your notes about this applicant..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSaveScore}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Save Score
                </button>

                <button
                  onClick={() => handlePreviewEmail('acceptance')}
                  disabled={!scores[selectedApplication.applicationId!]}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Accept Email
                </button>

                <button
                  onClick={() => handlePreviewEmail('waitlist')}
                  disabled={!scores[selectedApplication.applicationId!]}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Waitlist Email
                </button>

                <button
                  onClick={() => handlePreviewEmail('rejection')}
                  disabled={!scores[selectedApplication.applicationId!]}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Reject Email
                </button>

                <button
                  onClick={() => handlePreviewEmail('onboarding')}
                  disabled={
                    !scores[selectedApplication.applicationId!] ||
                    selectedApplication.status !== 'accepted'
                  }
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Preview Onboarding Email
                </button>
              </div>

              {/* Application Details (Collapsible Sections) */}
              <div className="mt-6 space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">Application Details</h3>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Basic Information</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Phone:</strong> {selectedApplication.phone}
                    </p>
                    <p>
                      <strong>Address:</strong> {selectedApplication.address}
                    </p>
                    <p>
                      <strong>Years in Lake Charles:</strong>{' '}
                      {selectedApplication.yearsInLakeCharles}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Community Involvement</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Organizations:</strong> {selectedApplication.organizations || 'N/A'}
                    </p>
                    <p>
                      <strong>Leadership Roles:</strong>{' '}
                      {selectedApplication.leadershipRoles || 'N/A'}
                    </p>
                    <p>
                      <strong>Volunteer Work:</strong> {selectedApplication.volunteerWork || 'N/A'}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Network & Reach</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Neighbors Reachable:</strong>{' '}
                      {selectedApplication.neighborsReachable || 'N/A'}
                    </p>
                    <p>
                      <strong>Social Media Following:</strong>{' '}
                      {selectedApplication.socialMediaFollowing || 'N/A'}
                    </p>
                    <p>
                      <strong>Community Connections:</strong>{' '}
                      {selectedApplication.communityConnections || 'N/A'}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Feedback Plan</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Strategy:</strong> {selectedApplication.feedbackStrategy || 'N/A'}
                    </p>
                    <p>
                      <strong>Frequency:</strong> {selectedApplication.feedbackFrequency || 'N/A'}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Communication Style</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>{selectedApplication.platformDescription || 'N/A'}</p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Time Commitment</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Hours Per Month:</strong> {selectedApplication.hoursPerMonth || 'N/A'}
                    </p>
                    <p>
                      <strong>Availability:</strong> {selectedApplication.availability || 'N/A'}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">Long-term Interest</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Governance Interest:</strong>{' '}
                      {selectedApplication.governanceInterest || 'N/A'}
                    </p>
                    <p>
                      <strong>Governance Roles:</strong>{' '}
                      {selectedApplication.governanceRoles || 'N/A'}
                    </p>
                  </div>
                </details>

                <details className="bg-gray-50 rounded-lg p-4">
                  <summary className="font-medium cursor-pointer">References</summary>
                  <div className="mt-3 space-y-2 text-sm">
                    <p>
                      <strong>Reference 1:</strong> {selectedApplication.reference1Name || 'N/A'} (
                      {selectedApplication.reference1Contact || 'N/A'})
                    </p>
                    <p>
                      <strong>Reference 2:</strong> {selectedApplication.reference2Name || 'N/A'} (
                      {selectedApplication.reference2Contact || 'N/A'})
                    </p>
                  </div>
                </details>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p>Select an application to review</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Preview Modal */}
      {showEmailPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-lg">
                Email Preview:{' '}
                {emailPreviewType.charAt(0).toUpperCase() + emailPreviewType.slice(1)}
              </h3>
              <button
                onClick={() => setShowEmailPreview(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailPreviewHtml) }} />
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <AlertTriangle className="h-4 w-4" />
                <span>This email will be sent to {selectedApplication?.email}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowEmailPreview(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {emailPreviewType !== 'onboarding' && (
                  <button
                    onClick={() => {
                      const statusMap = {
                        acceptance: 'accepted',
                        waitlist: 'waitlisted',
                        rejection: 'rejected',
                      } as const;
                      handleSendEmailAndUpdateStatus(emailPreviewType, statusMap[emailPreviewType]);
                    }}
                    disabled={sendingEmail}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {sendingEmail ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Email & Update Status
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
